export interface TramaClientConfig {
  apiKey: string;
  projectId: string;
  baseUrl?: string;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined>;
}

export class TramaClient {
  private readonly baseUrl: string;

  constructor(private readonly config: TramaClientConfig) {
    this.baseUrl = (config.baseUrl ?? 'https://api.gotrama.com').replace(/\/$/, '');
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const url = new URL(`${this.baseUrl}${path}`);

    if (options.params) {
      for (const [key, value] of Object.entries(options.params)) {
        if (value !== undefined) url.searchParams.set(key, String(value));
      }
    }

    const response = await fetch(url.toString(), {
      method: options.method ?? 'GET',
      headers: {
        'x-api-key': this.config.apiKey,
        'Content-Type': 'application/json',
        'x-project-id': this.config.projectId,
      },
      ...(options.body != null ? { body: JSON.stringify(options.body) } : {}),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: { message: 'Unknown error' } })) as { error?: { message?: string; code?: string } };
      throw new Error(error.error?.message ?? `HTTP ${response.status}`);
    }

    const data = await response.json() as { success: boolean; data: T };
    return data.data;
  }

  
  async verifyMember(memberToken: string): Promise<{
    platformId: string;
    platform: 'wix' | 'shopify' | 'webflow';
    email: string | null;
    firstName: string | null;
    lastName: string | null;
    tags: string[];
    verifiedAt: string;
    metadata: Record<string, unknown>;
  } | null> {
    try {
      return await this.request('/api/v1/members/verify', {
        method: 'POST',
        body: { projectId: this.config.projectId, memberToken },
      });
    } catch (err) {
      if (err instanceof Error && /invalid or expired/i.test(err.message)) return null;
      throw err;
    }
  }

  
  async getCmsCollections(): Promise<Array<{ id: string; name: string; slug: string | null }>> {
    return this.request('/api/v1/cms/collections', {
      params: { projectId: this.config.projectId },
    });
  }
}
